// Procedural horror soundscape via Web Audio (no audio files).
let ac, master, noiseBuf, ch = {}, crackleTimer, tickTimer, nextAmbient = 0;
let muted = localStorage.getItem("hc_muted") === "1";
let route = null;
const bus = {};
const DEFAULT_VOL = { storm: 0.8, effects: 0.8, scares: 0.8 };
let vol = { ...DEFAULT_VOL, ...JSON.parse(localStorage.getItem("hc_vol") || "{}") };
export const getVolumes = () => ({ ...vol });
export function setVolume(key, v) {
  vol[key] = v; localStorage.setItem("hc_vol", JSON.stringify(vol));
  if (ac && bus[key]) bus[key].gain.setTargetAtTime(v, ac.currentTime, 0.05);
}
const out = () => route || bus.effects;
const lv = { fire: 0, clock: 0 };

const noise = (sec) => {
  const b = ac.createBuffer(1, ac.sampleRate * sec, ac.sampleRate), d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
};
const loopNoise = () => { const s = ac.createBufferSource(); s.buffer = noiseBuf; s.loop = true; s.start(); return s; };
const filt = (type, f, q = 1) => { const n = ac.createBiquadFilter(); n.type = type; n.frequency.value = f; n.Q.value = q; return n; };
const gain = (v = 0) => { const g = ac.createGain(); g.gain.value = v; return g; };
const lfo = (rate, depth, target) => { const o = ac.createOscillator(), g = gain(depth); o.frequency.value = rate; o.connect(g).connect(target); o.start(); };

function burst(f, dur, vol, type = "lowpass", q = 1, delay = 0) {
  const t = ac.currentTime + delay, s = ac.createBufferSource(), g = gain(0);
  s.buffer = noiseBuf; s.connect(filt(type, f, q)).connect(g).connect(out());
  g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  s.start(t, Math.random() * 2, dur + 0.05);
}

function creak(from, to, dur, vol, delay = 0) {
  const t = ac.currentTime + delay, o = ac.createOscillator(), g = gain(0), bp = filt("bandpass", 700, 3);
  o.type = "sawtooth"; o.frequency.setValueAtTime(from, t); o.frequency.linearRampToValueAtTime(to, t + dur);
  const v = ac.createOscillator(), vg = gain(from * 0.08); v.frequency.value = 22 + Math.random() * 10; v.connect(vg).connect(o.frequency); v.start(t); v.stop(t + dur);
  o.connect(bp).connect(g).connect(out());
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol, t + dur * 0.25); g.gain.linearRampToValueAtTime(vol * 0.6, t + dur * 0.7); g.gain.linearRampToValueAtTime(0, t + dur);
  o.start(t); o.stop(t + dur + 0.05);
}

function tone(freq, start, dur, vol, type = "sine") {
  const t = ac.currentTime + start, o = ac.createOscillator(), g = gain(0);
  o.type = type; o.frequency.value = freq; o.connect(g).connect(out());
  g.gain.linearRampToValueAtTime(vol, t + 0.05); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.start(t); o.stop(t + dur + 0.05);
}

export function initAudio() {
  if (ac) { ac.resume(); return; }
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  ac = new Ctx(); master = gain(muted ? 0 : 0.85); master.connect(ac.destination); noiseBuf = noise(3);
  Object.keys(DEFAULT_VOL).forEach((k) => { bus[k] = gain(vol[k]); bus[k].connect(master); });

  ch.wind = gain(); ch.wind.connect(bus.storm);
  const wsrc = loopNoise(), bp = filt("bandpass", 450, 0.7), howl = filt("bandpass", 650, 9), hg = gain(0.9);
  lfo(0.11, 260, bp.frequency); lfo(0.07, 280, howl.frequency); lfo(0.05, 0.6, hg.gain);
  wsrc.connect(bp).connect(ch.wind); wsrc.connect(howl).connect(hg).connect(ch.wind);

  ch.fire = gain(); ch.fire.connect(bus.effects);
  loopNoise().connect(filt("lowpass", 380)).connect(ch.fire);

  ch.hum = gain(); ch.hum.connect(bus.effects);
  [[55, "sine", 0.6], [110, "triangle", 0.25], [57, "sine", 0.4]].forEach(([f, ty, v]) => {
    const o = ac.createOscillator(), g = gain(v); o.type = ty; o.frequency.value = f; o.connect(g).connect(ch.hum); o.start();
  });

  ch.static = gain(); ch.static.connect(bus.effects);
  loopNoise().connect(filt("highpass", 3000)).connect(ch.static);

  const crackle = () => {
    if (lv.fire > 0.01) { burst(2500 + Math.random() * 3000, 0.015 + Math.random() * 0.03, lv.fire * (0.3 + Math.random() * 0.9), "highpass"); if (Math.random() < 0.08) burst(900, 0.12, lv.fire * 0.8, "bandpass", 2); }
    crackleTimer = setTimeout(crackle, 35 + Math.random() * 150);
  };
  crackle();
  let tock = false;
  tickTimer = setInterval(() => { if (lv.clock > 0.01) { tock = !tock; burst(tock ? 1800 : 2600, 0.03, lv.clock, "bandpass", 6); } }, 1000);
}

export function setMuted(m) {
  muted = m; localStorage.setItem("hc_muted", m ? "1" : "0");
  if (ac) master.gain.setTargetAtTime(m ? 0 : 0.85, ac.currentTime, 0.1);
}
export const isMuted = () => muted;
export function suspendAudio() { if (ac) ac.suspend(); }

const near = (room, types, px, range) => {
  let best = 0;
  room.props.forEach((p) => { if (types.includes(p.type)) best = Math.max(best, 1 - Math.abs(p.x + 12 - px) / range); });
  return Math.max(0, best);
};
const set = (node, v) => node.gain.setTargetAtTime(v, ac.currentTime, 0.4);

export function updateAudio(st, room) {
  if (!ac || ac.state !== "running" || ch.drive) return;
  const px = st.x + 4, basement = room.floor === "basement";
  let win = 0;
  room.windows.forEach((w) => { win = Math.max(win, 1 - Math.abs(w.x + 17 - px) / 120); });
  const wind = room.outside ? 0.55 : basement ? 0.025 : 0.07 + (room.floor === "upper" ? 0.04 : 0) + Math.max(0, win) * 0.16 + (room.doors.some((d) => d.kind === "front") ? 0.04 : 0);
  set(ch.wind, wind);
  lv.fire = near(room, ["fireplace"], px, 170) * 0.5;
  set(ch.fire, lv.fire * 0.35);
  set(ch.hum, basement ? 0.025 + near(room, ["furnace", "waterheater"], px, 140) * 0.07 : 0);
  set(ch.static, near(room, ["tv"], px, 110) * 0.05);
  lv.clock = near(room, ["clock"], px, 150) * 0.25;
  const now = ac.currentTime;
  if (!nextAmbient) nextAmbient = now + 15;
  if (now > nextAmbient && !room.outside) {
    nextAmbient = now + 18 + Math.random() * 30;
    scare();
  }
}

export function scare() {
  if (!ac || ac.state !== "running") return;
  const r = Math.random();
    route = bus.scares;
    if (r < 0.4) creak(130 + Math.random() * 60, 90, 1.2, 0.04);
    else if (r < 0.7) [0, 0.35, 0.7].forEach((d) => burst(160, 0.18, 0.12, "lowpass", 1, d));
    else burst(500, 1.6, 0.05, "bandpass", 1.5);
    route = null;
}

export function stepSound(room) {
  if (!ac || ac.state !== "running") return;
  const p = room.outside ? "snow" : room.theme.pattern;
  if (p === "snow") { burst(1400, 0.14, 0.12, "bandpass", 0.8); return; }
  burst(p === "concrete" ? 220 : 160, 0.07, p === "tile" ? 0.12 : 0.2);
  if (p === "tile") burst(3000, 0.02, 0.03, "highpass");
  if ((p === "logs" || p === "stripes") && Math.random() < 0.14) creak(150 + Math.random() * 90, 95 + Math.random() * 30, 0.35 + Math.random() * 0.4, 0.07);
}

export function doorSound(kind) {
  if (!ac || ac.state !== "running") return;
  if (kind === "stairs_up" || kind === "stairs_down") { [0, 0.18, 0.36, 0.54].forEach((d) => burst(170, 0.08, 0.2, "lowpass", 1, d)); creak(120, 90, 0.5, 0.05, 0.2); return; }
  if (kind === "front") burst(700, 0.9, 0.25, "bandpass", 0.8);
  creak(85, 170, 0.9, 0.09); burst(120, 0.25, 0.35, "lowpass", 1, 0.85);
}

export function lockedSound() {
  if (!ac || ac.state !== "running") return;
  [0, 0.09, 0.2, 0.28, 0.4].forEach((d) => burst(2200 + Math.random() * 800, 0.05, 0.18, "bandpass", 8, d));
  burst(90, 0.4, 0.3, "lowpass", 1, 0.5);
}

export function pickupSound() {
  if (!ac || ac.state !== "running") return;
  tone(440, 0, 1.2, 0.06, "triangle"); tone(622.25, 0.18, 1.4, 0.05, "triangle"); tone(220, 0, 1.6, 0.04);
}

export function cutsceneAudio(kind) {
  if (!ac) return;
  if (!ch.engine) {
    ch.engine = gain(); ch.engine.connect(bus.effects);
    const o = ac.createOscillator(), lp = filt("lowpass", 180); o.type = "sawtooth"; o.frequency.value = 46;
    lfo(0.6, 3, o.frequency); o.connect(lp).connect(ch.engine); o.start();
    ch.drone = gain(); ch.drone.connect(bus.scares);
    [55, 82.4, 110.6, 164.8].forEach((f, i) => {
      const d = ac.createOscillator(), g = gain(0.25); d.type = i % 2 ? "triangle" : "sawtooth"; d.frequency.value = f;
      lfo(0.2 + i * 0.07, 2, d.frequency); d.connect(filt("lowpass", 420)).connect(g).connect(ch.drone); d.start();
    });
  }
  ch.drive = !!kind;
  set(ch.engine, kind === "drive" ? 0.14 : 0);
  set(ch.drone, ["ritual", "watcher", "quake", "wolves", "assault", "godrise"].includes(kind) ? 0.12 : 0);
  if (kind) {
    set(ch.wind, kind === "drive" ? 0.45 : 0.18); set(ch.hum, 0); set(ch.static, 0); lv.clock = 0;
    lv.fire = kind === "ritual" ? 0.6 : 0; set(ch.fire, kind === "ritual" ? 0.3 : 0);
  }
}

export function sting() {
  if (!ac || ac.state !== "running") return;
  route = bus.scares;
  tone(98, 0, 2.6, 0.08, "sawtooth"); tone(103.8, 0.05, 2.6, 0.07, "sawtooth"); tone(196, 0.1, 1.8, 0.03, "triangle");
  burst(400, 1.6, 0.06, "bandpass", 1);
  route = null;
}

export function drinkSound() {
  if (!ac || ac.state !== "running") return;
  burst(300, 0.12, 0.12, "bandpass", 3); burst(240, 0.14, 0.14, "bandpass", 3, 0.35); burst(1800, 0.25, 0.04, "highpass", 1, 0.8);
}

export function beepSound() {
  if (!ac || ac.state !== "running") return;
  tone(988, 0, 0.18, 0.035, "sine");
}
